import React, { useState, useEffect } from "react";
import {
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  Image as ImageIcon,
  Type,
} from "lucide-react";
import api from "../../../utils/api";

const HomePageAdsSection = ({ showCustomDialog, showConfirmationDialog }) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdModal, setShowAdModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [adForm, setAdForm] = useState({
    adType: "image",
    imageUrl: "",
    description: "",
    isActive: true,
    displayOrder: 0,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/home-page-ads");
      setAds(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching ads:", error);
      setAds([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNewAdModal = () => {
    setEditingAd(null);
    setAdForm({
      adType: "image",
      imageUrl: "",
      description: "",
      isActive: true,
      displayOrder: ads.length, // Set to next position
    });
    setImageFile(null);
    setImagePreview("");
    setShowAdModal(true);
  };

  const handleEditAd = (ad) => {
    setEditingAd(ad);
    setAdForm({
      adType: ad.adType,
      imageUrl: ad.imageUrl || "",
      description: ad.description || "",
      isActive: ad.isActive,
      displayOrder: ad.displayOrder || 0,
    });
    setImageFile(null);
    setImagePreview(ad.imageUrl || "");
    setShowAdModal(true);
  };

  const handleDeleteAd = (adId) => {
    const deleteAction = async () => {
      try {
        await api.delete(`/api/home-page-ads/${adId}`);
        setAds(ads.filter((ad) => ad._id !== adId));
        showCustomDialog("Ad deleted successfully!");
      } catch (error) {
        console.error("Error deleting ad:", error);
        showCustomDialog("Failed to delete ad", "error");
      }
    };

    showConfirmationDialog(
      "Are you sure you want to delete this ad?",
      deleteAction
    );
  };

  // eslint-disable-next-line no-unused-vars
  const handleToggleActive = async (ad) => {
    try {
      const response = await api.patch(
        `/api/home-page-ads/${ad._id}/toggle-active`
      );
      setAds(ads.map((a) => (a._id === ad._id ? response.data : a)));
      showCustomDialog(
        `Ad ${
          response.data.isActive ? "activated" : "deactivated"
        } successfully!`
      );
    } catch (error) {
      console.error("Error toggling ad status:", error);
      showCustomDialog("Failed to toggle ad status", "error");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleMoveUp = async (ad, index) => {
    if (index === 0) return; // Already at top

    try {
      // Swap display orders
      const prevAd = ads[index - 1];
      const currentOrder = ad.displayOrder;
      const prevOrder = prevAd.displayOrder;

      // Update both ads
      await Promise.all([
        api.put(`/api/home-page-ads/${ad._id}`, {
          ...ad,
          displayOrder: prevOrder,
        }),
        api.put(`/api/home-page-ads/${prevAd._id}`, {
          ...prevAd,
          displayOrder: currentOrder,
        }),
      ]);

      // Refresh ads list
      fetchAds();
      showCustomDialog("Ad moved up successfully!");
    } catch (error) {
      console.error("Error moving ad up:", error);
      showCustomDialog("Failed to move ad up", "error");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleMoveDown = async (ad, index) => {
    if (index === ads.length - 1) return; // Already at bottom

    try {
      // Swap display orders
      const nextAd = ads[index + 1];
      const currentOrder = ad.displayOrder;
      const nextOrder = nextAd.displayOrder;

      // Update both ads
      await Promise.all([
        api.put(`/api/home-page-ads/${ad._id}`, {
          ...ad,
          displayOrder: nextOrder,
        }),
        api.put(`/api/home-page-ads/${nextAd._id}`, {
          ...nextAd,
          displayOrder: currentOrder,
        }),
      ]);

      // Refresh ads list
      fetchAds();
      showCustomDialog("Ad moved down successfully!");
    } catch (error) {
      console.error("Error moving ad down:", error);
      showCustomDialog("Failed to move ad down", "error");
    }
  };

  const handleAdInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAdForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAdTypeChange = (type) => {
    setAdForm((prev) => ({
      ...prev,
      adType: type,
    }));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 20MB)
      if (file.size > 20 * 1024 * 1024) {
        showCustomDialog("Image size must be less than 20MB", "error");
        return;
      }

      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview("");
    setAdForm((prev) => ({ ...prev, imageUrl: "" }));
  };

  const uploadImageToCloudinary = async () => {
    if (!imageFile) return adForm.imageUrl;

    try {
      setUploadingImage(true);

      // Step 1: Get upload signature
      const signatureResponse = await api.post("/api/upload/signature");
      const { signature, timestamp, cloudName, apiKey, uploadParams } =
        signatureResponse.data;

      // Step 2: Upload to Cloudinary
      const formData = new FormData();
      formData.append("file", imageFile);
      formData.append("signature", signature);
      formData.append("timestamp", timestamp);
      formData.append("api_key", apiKey);

      Object.keys(uploadParams).forEach((key) => {
        if (key !== "timestamp") {
          formData.append(key, uploadParams[key]);
        }
      });

      const cloudinaryResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!cloudinaryResponse.ok) {
        throw new Error("Failed to upload image to Cloudinary");
      }

      const cloudinaryResult = await cloudinaryResponse.json();
      return cloudinaryResult.secure_url;
    } catch (error) {
      console.error("Image upload error:", error);
      throw new Error("Failed to upload image. Please try again.");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmitAd = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      // Upload image if present
      let imageUrl = adForm.imageUrl;
      if (imageFile) {
        imageUrl = await uploadImageToCloudinary();
      }

      const submitData = {
        adType: adForm.adType,
        imageUrl: adForm.adType === "image" ? imageUrl : null,
        description: adForm.description,
        isActive: adForm.isActive,
        displayOrder: adForm.displayOrder,
      };

      // Validate
      if (adForm.adType === "image" && !imageUrl) {
        showCustomDialog("Please upload an image for image-based ads", "error");
        setFormLoading(false);
        return;
      }

      if (adForm.adType === "text" && !adForm.description) {
        showCustomDialog(
          "Please enter description for text-based ads",
          "error"
        );
        setFormLoading(false);
        return;
      }

      if (editingAd) {
        const response = await api.put(
          `/api/home-page-ads/${editingAd._id}`,
          submitData
        );
        setAds(
          ads.map((ad) => (ad._id === editingAd._id ? response.data : ad))
        );
        showCustomDialog("Ad updated successfully!");
      } else {
        const response = await api.post("/api/home-page-ads", submitData);
        setAds([response.data, ...ads]);
        showCustomDialog("Ad created successfully!");
      }

      setShowAdModal(false);
      setAdForm({
        adType: "image",
        imageUrl: "",
        description: "",
        isActive: true,
        displayOrder: 0,
      });
      setImageFile(null);
      setImagePreview("");
    } catch (error) {
      console.error("Error saving ad:", error);
      showCustomDialog(
        error.response?.data?.message || error.message || "Failed to save ad",
        "error"
      );
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 sm:mb-0">
            Home Page Ads
          </h2>
          <button
            onClick={handleOpenNewAdModal}
            className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Ad
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading ads...</p>
          </div>
        ) : ads.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              No Ads Yet
            </h3>
            <p className="text-gray-500 mb-6">
              Add a new home page ad to get started
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Preview
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ads.map((ad, index) => (
                  <tr key={ad._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-gray-900">
                        {ad.displayOrder}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {ad.adType === "image" ? (
                          <ImageIcon className="w-5 h-5 text-blue-600 mr-2" />
                        ) : (
                          <Type className="w-5 h-5 text-green-600 mr-2" />
                        )}
                        <span className="text-sm font-medium text-gray-900 capitalize">
                          {ad.adType}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {ad.adType === "image" && ad.imageUrl ? (
                        <img
                          src={ad.imageUrl}
                          alt="Ad preview"
                          className="h-16 w-24 object-cover rounded"
                        />
                      ) : (
                        <span className="text-sm text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {ad.description || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          ad.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {ad.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {/* <button
                          onClick={() => handleMoveUp(ad, index)}
                          disabled={index === 0}
                          className={`${
                            index === 0
                              ? "text-gray-300 cursor-not-allowed"
                              : "text-purple-600 hover:text-purple-900"
                          }`}
                          title="Move Up"
                        >
                          <ChevronUp className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleMoveDown(ad, index)}
                          disabled={index === ads.length - 1}
                          className={`${
                            index === ads.length - 1
                              ? "text-gray-300 cursor-not-allowed"
                              : "text-purple-600 hover:text-purple-900"
                          }`}
                          title="Move Down"
                        >
                          <ChevronDown className="w-5 h-5" />
                        </button> */}
                        {/* <button
                          onClick={() => handleToggleActive(ad)}
                          className="text-yellow-600 hover:text-yellow-900"
                          title={ad.isActive ? "Deactivate" : "Activate"}
                        >
                          {ad.isActive ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button> */}
                        <button
                          onClick={() => handleEditAd(ad)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteAd(ad._id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Ad Modal */}
      {showAdModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-auto my-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">
                {editingAd ? "Edit Ad" : "New Ad"}
              </h3>
              <button
                onClick={() => setShowAdModal(false)}
                className="text-gray-500 hover:text-gray-700"
                disabled={formLoading || uploadingImage}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmitAd}>
              {/* Ad Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Ad Type *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="adType"
                      value="image"
                      checked={adForm.adType === "image"}
                      onChange={(e) => handleAdTypeChange(e.target.value)}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Image Based Ad
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="adType"
                      value="text"
                      checked={adForm.adType === "text"}
                      onChange={(e) => handleAdTypeChange(e.target.value)}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Text Based Ad
                    </span>
                  </label>
                </div>
              </div>

              {/* Image Upload (for image type) */}
              {adForm.adType === "image" && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Image *
                  </label>
                  <div className="space-y-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-red-50 file:text-red-700
                        hover:file:bg-red-100
                        cursor-pointer"
                    />
                    <p className="text-xs text-gray-500">
                      PNG, JPG, GIF (MAX. 20MB)
                    </p>
                    {imagePreview && (
                      <div className="relative mt-3">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-full max-h-64 object-contain rounded-lg border border-gray-300"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700 shadow-lg"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Description */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description {adForm.adType === "image" && "(Optional)"}
                  {adForm.adType === "text" && "*"}
                </label>
                <textarea
                  name="description"
                  value={adForm.description}
                  onChange={handleAdInputChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Enter description..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Tip: Add clickable links using: [Link
                  Text](https://url.com)
                </p>
              </div>

              {/* Display Order */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  name="displayOrder"
                  value={adForm.displayOrder}
                  onChange={handleAdInputChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lower numbers appear first. Use 0, 1, 2, 3... to control
                  order.
                </p>
              </div>

              {/* Is Active */}
              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={adForm.isActive}
                    onChange={handleAdInputChange}
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Active (Show this ad on homepage)
                  </span>
                </label>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAdModal(false)}
                  className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400 transition-colors"
                  disabled={formLoading || uploadingImage}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={formLoading || uploadingImage}
                >
                  {formLoading || uploadingImage ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {uploadingImage ? "Uploading..." : "Saving..."}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {editingAd ? "Update" : "Create"} Ad
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePageAdsSection;
